import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export interface Crumb {
  label: ReactNode;
  /** Absent for the current page. */
  to?: string;
}

interface PageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side of the page header: counts, buttons. */
  actions?: ReactNode;
  /** The breadcrumb trail above the title (prototype `.crumbs`); the last one is the page itself. */
  crumbs?: Crumb[];
  /** The "Back · From …" strip above the crumbs (prototype `.frombar`), for pages opened from another. */
  back?: { to: string; from: ReactNode };
  children?: ReactNode;
}

/**
 * One page inside the shell: its back strip, breadcrumbs, title, subtitle and actions, then its
 * content. The shell around it (ShellLayout) is rendered once by the router, not by each page.
 */
export function Page({ title, subtitle, actions, crumbs, back, children }: PageProps) {
  return (
    <>
      {back || crumbs ? (
        <div className="flex flex-col gap-2.5">
          {back ? (
            <div className="border-line bg-cyan-bg text-muted flex items-center gap-2.5 rounded-lg border px-2.5 py-1.5 text-xs">
              <Link
                to={back.to}
                className="text-blue inline-flex items-center gap-1 font-semibold hover:underline"
              >
                <ArrowLeft size={13} aria-hidden="true" /> Back
              </Link>
              <span>
                From <b className="text-ink">{back.from}</b>
              </span>
            </div>
          ) : null}
          {crumbs ? (
            <nav
              aria-label="Breadcrumb"
              className="text-muted flex flex-wrap items-center gap-1.5 text-xs"
            >
              {crumbs.map((crumb, index) => (
                <span key={index} className="inline-flex items-center gap-1.5">
                  {index > 0 ? <span aria-hidden="true">›</span> : null}
                  {crumb.to ? (
                    <Link to={crumb.to} className="hover:text-cyan">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span aria-current={index === crumbs.length - 1 ? 'page' : undefined}>
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1 md:min-w-[280px]">
          <h1 className="font-display text-[22px] font-semibold leading-tight tracking-[-0.01em]">
            {title}
          </h1>
          {subtitle ? <div className="text-muted mt-1">{subtitle}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </>
  );
}
